import React, { useState, useMemo } from "react";
import { Plane, Wrench, Headphones, Helicopter, GraduationCap, MapPin, Mail, Phone, Clock, Building2, Search, X, Tag, Key, Compass, ChevronRight, Sparkles } from "lucide-react";
import { useNavigation, PageName } from "@/context/NavigationContext";
import { useAuth } from "@/context/AuthContext";
import { useAppContext } from "@/context/AppContext";
import heroImage from "@/assets/hero-aviation.jpg";
import SearchOverlay from "@/components/SearchOverlay";

const iconMap = {
  Plane,
  Wrench,
  Headphones,
  Helicopter,
  GraduationCap
};

const Home = () => {
  const { navigateTo } = useNavigation();
  const {
      enabledHomeCardFunctions,
      getDictValue,
      appConfigMap,
      aircraftListingsCount,
      aircraftRentalsCount,
      aircraftPartsCount,
      experiencesCount
    } = useAuth();
  const { aircraftListings, aircraftRentals, aircraftParts, experiences, queryLightRag } = useAppContext();
  const [searchQuery, setSearchQuery] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [searchOverlayOpen, setSearchOverlayOpen] = useState(false);
  const [overlayQuery, setOverlayQuery] = useState("");

  // 🟢 LOGICĂ FILTRARE GLOBALĂ: DEEP SEARCH + EXCLUDEREA STRICTĂ A LISTĂRILOR PROPRII
    const searchResults = useMemo(() => {
      if (!searchQuery.trim()) return [];
      const query = searchQuery.toLowerCase();

      const { myAircraftListings, myAircraftRentals, myAircraftParts, myExperiences } = useAuth() as any;

      const matchesAnyField = (obj: any): boolean => {
        if (obj === null || obj === undefined) return false;
        if (typeof obj === "string") return obj.toLowerCase().includes(query);
        if (typeof obj === "number" || typeof obj === "boolean") return String(obj).toLowerCase().includes(query);
        if (Array.isArray(obj)) return obj.some(item => matchesAnyField(item));
        if (typeof obj === "object") return Object.values(obj).some(val => matchesAnyField(val));
        return false;
      };

      const isMyListing = (id: any) => myAircraftListings?.some((l: any) => Number(l.listingId) === Number(id));
      const isMyRental = (id: any) => myAircraftRentals?.some((r: any) => Number(r.rentalId) === Number(id));
      const isMyPart = (id: any) => myAircraftParts?.some((p: any) => Number(p.partId) === Number(id));
      const isMyExperience = (id: any) => myExperiences?.some((e: any) => Number(e.experienceId) === Number(id));

      const matchedSales = aircraftListings
        .filter(item => item.verified && !isMyListing(item.listingId) && matchesAnyField(item))
        .map(item => ({
          ...item,
          type: "SALE",
          id: item.listingId,
          route: "aircraft-details",
          routeParam: { listingId: item.listingId, aircraftListing: item }
        }));

      const matchedRentals = aircraftRentals
        .filter(item => item.verified && !isMyRental(item.rentalId) && matchesAnyField(item))
        .map(item => ({
          ...item,
          type: "RENTAL",
          id: item.rentalId,
          route: "rental-details",
          routeParam: { rentalId: item.rentalId, aircraftRental: item }
        }));

      const matchedParts = aircraftParts
        .filter(item => item.verified && !isMyPart(item.partId) && matchesAnyField(item))
        .map(item => ({
          ...item,
          type: "PART",
          id: item.partId,
          route: "part-details",
          routeParam: { partId: item.partId, part: item }
        }));

      const matchedExperiences = experiences
        .filter(item => item.verified && !isMyExperience(item.experienceId) && matchesAnyField(item))
        .map(item => ({
          ...item,
          type: "EXPERIENCE",
          id: item.experienceId,
          route: "experience-details",
          routeParam: { experienceId: item.experienceId, experience: item }
        }));

      return [...matchedSales, ...matchedRentals, ...matchedParts, ...matchedExperiences];
    }, [searchQuery, aircraftListings, aircraftRentals, aircraftParts, experiences]);

  return (
    <div className="animate-fade-in">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden py-20">
        <div className="absolute inset-0">
          <img src={heroImage} alt="Aviation hero" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-background/30" />
        </div>

        <div className="relative container text-center space-y-10 max-w-7xl mx-auto z-10">
          <div className="space-y-6">
            <h1 className="text-5xl md:text-7xl font-display font-bold leading-tight">
              <span className="text-gradient-sky">{getDictValue("app.name")}</span>
            </h1>

            <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto font-light">
              {getDictValue("home.description")}
            </p>
          </div>

          {/* BARĂ DE CĂUTARE AI LIGHTRAG */}
                    <div className="relative max-w-xl mx-auto px-4">
                      <form
                        onSubmit={async (e) => {
                          e.preventDefault();
                          if (!searchQuery.trim()) return;

                          // Apelăm funcția LightRAG din contextul aplicației
                          // Câmpurile cerute: ["titlu", "id", "media", "categorie"]
                          const results = await queryLightRag(searchQuery);

                          // Opțional: Poți trimite rezultatele către un overlay dedicat sau o pagină de rezultate AI
                          console.log("Rezultate LightRAG:", results);
                        }}
                        className={`relative flex items-center bg-white/5 backdrop-blur-md border rounded-2xl h-14 px-4 transition-all duration-300 ${
                          isFocused ? "border-sky-500/50 shadow-[0_0_25px_rgba(14,165,233,0.15)]" : "border-white/10"
                        }`}
                      >
                        {/* Iconiță AI Pilot / Capitan */}
                        <div className="flex items-center justify-center h-8 w-8 rounded-xl bg-sky-500/10 border border-sky-500/20 text-sky-400 shrink-0 mr-3">
                          <Sparkles className="h-4 w-4 animate-pulse" />
                        </div>

                        <input
                          type="text"
                          value={searchQuery}
                          onFocus={() => setIsFocused(true)}
                          onBlur={() => setTimeout(() => setIsFocused(false), 200)}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Întreabă copilotul AI (ex: Vreau un elicopter ușor pentru zboruri montane...)"
                          className="w-full h-full bg-transparent text-sm text-white border-0 outline-none focus:ring-0 px-1 placeholder:text-slate-400 font-sans"
                        />

                        {searchQuery && (
                          <button
                            type="button"
                            onClick={() => setSearchQuery("")}
                            className="text-slate-500 hover:text-white transition-colors mr-2"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}

                        {/* Buton de trimitere interogare AI */}
                        <button
                          type="submit"
                          className="h-9 px-4 rounded-xl bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold text-xs uppercase tracking-wider transition-all duration-200 shrink-0 flex items-center gap-1.5 shadow-lg shadow-sky-500/20"
                        >
                          <span>Căutare AI</span>
                          <ChevronRight size={14} />
                        </button>
                      </form>
                    </div>

          {/* Cards Grid */}
          <div className="flex flex-wrap justify-center gap-4 pt-4">
            {enabledHomeCardFunctions?.map((item) => {
              const details = item.jsonDetails ? JSON.parse(item.jsonDetails) : {};
              const Icon = iconMap[details.icon as keyof typeof iconMap] || Plane;
              const action = item.action; // e.g. "aircraft", "rental", "part", "experience"

              // 🟢 Generate database matching dictionary keys dynamically based on the action name
              const titleKey = `home.card.${action}.title`;
              const descKey = `home.card.${action}.description`;

              // 🟢 Fallback to item values if dictionary entry is missing
              const dynamicTitle = getDictValue(titleKey) || item.label;
              const dynamicDescription = getDictValue(descKey) || details.description;

              return (
                <div
                  key={item.id}
                  onClick={() => navigateTo(action as PageName)}
                  className="w-full sm:flex-1 sm:min-w-[250px] sm:max-w-[320px] category-card text-center cursor-pointer group bg-white/5 backdrop-blur-md border border-white/10 shadow-xl rounded-2xl p-6 transition-all duration-300 hover:bg-white/10"
                >
                  <Icon className="h-10 w-10 mx-auto mb-3 text-primary group-hover:text-accent transition-colors" />

                  <h3 className="font-display font-semibold mb-1">{dynamicTitle}</h3>
                  <p className="text-xs text-muted-foreground mb-2">{dynamicDescription}</p>

                  <span className="text-xs text-accent font-medium">
                                      {(() => {
                                        if (action === "aircraft") return aircraftListingsCount;
                                        if (action === "rental") return aircraftRentalsCount;
                                        if (action === "part") return aircraftPartsCount;
                                        if (action === "experience") return experiencesCount;
                                        return details.listings || 0;
                                      })()} produse
                                    </span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTIUNEA DE CONTACT DINAMICĂ FORMATA PRIN CONFIGURARI DE TIP SECTION */}
      <section id="contact-section" className="container max-w-7xl mx-auto px-6 pt-16 pb-8 border-t border-border/20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">

          {/* Partea Stanga: Sumar Text Dinamic */}
          <div className="lg:col-span-4 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent text-xs font-mono font-bold uppercase tracking-wider">
              {getDictValue("app.motto")}
            </div>
            <h2 className="text-3xl font-display font-black text-foreground tracking-tight uppercase italic">
              {getDictValue("contact.title")}
            </h2>
            <p className="text-sm text-muted-foreground font-medium leading-relaxed">
              <span className="text-foreground font-bold">{getDictValue("app.name")}</span><br />
              {getDictValue("contact.description")}
            </p>
          </div>

          {/* Partea Dreapta: Grid Date de Contact Dinamice */}
          {/* 🟢 MODIFICARE: Design minimalist tip „Telemetrie” cu linii orizontale în loc de carduri masive */}
          <div className="lg:col-span-8 bg-white/[0.02] border border-white/5 rounded-3xl p-6 md:p-8 backdrop-blur-md space-y-6">

            {/* Rând: Sediu */}
            {getDictValue("contact.value.address") && (
              <div className="flex items-start sm:items-center gap-4 pb-5 border-b border-white/5 group">
                <div className="p-2.5 bg-white/5 rounded-xl border border-white/10 text-slate-400 group-hover:text-white transition-colors shrink-0">
                  <Building2 size={16} />
                </div>
                <div className="flex-1 sm:flex sm:items-center sm:justify-between gap-4">
                  <h4 className="text-[10px] text-slate-500 font-mono font-bold uppercase tracking-wider min-w-[120px]">{getDictValue("contact.label.address")}</h4>
                  <p className="text-sm text-slate-300 font-semibold tracking-wide sm:text-right whitespace-pre-line">
                    {getDictValue("contact.value.address")}
                  </p>
                </div>
              </div>
            )}

            {/* Rând: Email */}
            {getDictValue("contact.value.email") && (
              <div className="flex items-start sm:items-center gap-4 pb-5 border-b border-white/5 group">
                <div className="p-2.5 bg-white/5 rounded-xl border border-white/10 text-slate-400 group-hover:text-white transition-colors shrink-0">
                  <Mail size={16} />
                </div>
                <div className="flex-1 sm:flex sm:items-center sm:justify-between gap-4">
                  <h4 className="text-[10px] text-slate-500 font-mono font-bold uppercase tracking-wider min-w-[120px]">{getDictValue("contact.label.email")}</h4>
                  <a
                    href={`mailto:${getDictValue("contact.value.email")}`}
                    className="text-sm text-slate-300 font-mono font-bold hover:text-accent transition-colors break-all sm:text-right"
                  >
                    {getDictValue("contact.value.email")}
                  </a>
                </div>
              </div>
            )}

            {/* Rând: Program */}
            {getDictValue("contact.value.schedule") && (
              <div className="flex items-start sm:items-center gap-4 group">
                <div className="p-2.5 bg-white/5 rounded-xl border border-white/10 text-slate-400 group-hover:text-white transition-colors shrink-0">
                  <Clock size={16} />
                </div>
                <div className="flex-1 sm:flex sm:items-center sm:justify-between gap-4">
                  <h4 className="text-[10px] text-slate-500 font-mono font-bold uppercase tracking-wider min-w-[120px]">{getDictValue("contact.label.schedule")}</h4>
                  <p className="text-sm text-slate-300 font-semibold tracking-wide sm:text-right">
                    {getDictValue("contact.value.schedule")}
                  </p>
                </div>
              </div>
            )}

          </div>
        </div>
      </section>

      {/* Footer Section */}
      <footer className="border-t border-border/50 py-8 bg-background">
        <div className="container grid grid-cols-1 md:grid-cols-3 items-center gap-8 text-sm text-muted-foreground">

          {/* Coloana 1: Logo */}
          <div className="flex justify-center md:justify-start">
                <div
                  className="flex items-center gap-2 cursor-pointer group transition-colors hover:text-foreground"
                  onClick={() => navigateTo("home")}
                >
                  <Plane className="h-5 w-5 text-accent transition-transform group-hover:-rotate-12" />
                  <span className="font-display font-semibold text-foreground tracking-tight">
                    {getDictValue("app.name")}
                  </span>
                  {appConfigMap?.get("application.version")?.keyValue && (
                    <span className="font-thin text-slate-400/70 text-xs ml-1">
                      -  {appConfigMap.get("application.version").keyValue}
                    </span>
                  )}
                </div>
              </div>

          {/* Coloana 2: ANPC */}
          <div className="flex justify-center items-center gap-6">
            <a href="https://anpc.ro/ce-este-sal/" target="_blank" rel="nofollow" className="transition-opacity hover:opacity-80">
              <img src="https://etamade-com.github.io/anpc-sal-sol-logo/anpc-sal.svg" alt="SAL" className="h-12 w-auto md:h-14" />
            </a>
            <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="nofollow" className="transition-opacity hover:opacity-80">
              <img src="https://etamade-com.github.io/anpc-sal-sol-logo/anpc-sol.svg" alt="SOL" className="h-12 w-auto md:h-14" />
            </a>
          </div>

          {/* Coloana 3: Copyright */}
          <div className="flex justify-center md:justify-end">
            <p className="text-xs font-medium tracking-wide">
              {getDictValue("footer.copyright")}
            </p>
          </div>

        </div>
      </footer>
      <SearchOverlay
        isOpen={searchOverlayOpen}
        onClose={() => setSearchOverlayOpen(false)}
        initialQuery={overlayQuery}
      />
    </div>
  );
};

export default Home;